import React from 'react';
import type { components } from '../../../../types/learnerApi';

type Config = components['schemas']['SanitizedCodeSubmissionConfig'];

export interface CodeSubmissionAnswer {
  code: string;
}

export interface CodeSubmissionViewProps {
  item: { id: string; points?: number; config: Config };
  mode: 'learner' | 'preview';
  value?: CodeSubmissionAnswer;
  onChange?: (value: CodeSubmissionAnswer) => void;
  disabled?: boolean;
}

// CodeMirror is loaded lazily to keep initial bundle lean
const LazyEditor = React.lazy(() =>
  import('./CodeMirrorEditor').then((m) => ({ default: m.default }))
);

const CodeSubmissionView: React.FC<CodeSubmissionViewProps> = ({
  item,
  mode,
  value,
  onChange,
  disabled,
}) => {
  const code = value?.code ?? item.config.starter_code ?? '';

  return (
    <div className="space-y-3">
      <p className="font-medium text-text-primary">{item.config.prompt}</p>
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded bg-surface-secondary border border-border text-xs font-mono text-text-secondary">
          {item.config.language}
        </span>
      </div>
      <React.Suspense
        fallback={
          <textarea
            value={code}
            onChange={(e) => {
              if (disabled || mode === 'preview') return;
              onChange?.({ code: e.target.value });
            }}
            disabled={disabled}
            rows={10}
            className="w-full px-3 py-2 rounded-lg border border-border bg-surface-secondary text-text-primary text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60"
          />
        }
      >
        <LazyEditor
          language={item.config.language}
          value={code}
          onChange={(v) => {
            if (disabled || mode === 'preview') return;
            onChange?.({ code: v });
          }}
          disabled={disabled}
        />
      </React.Suspense>
      {item.points != null && (
        <p className="text-xs text-text-tertiary">{item.points} pt{item.points !== 1 ? 's' : ''} — manually graded</p>
      )}
    </div>
  );
};

export default CodeSubmissionView;
